import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Sparkles, ShirtIcon, CheckCircle2, MapPin, Users, Award, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const divisions = [
    {
      name: 'Aumkar International',
      location: 'Dundigal, India',
      description: 'Advanced packaging manufacturing including cardboard boxes, wrappers, and custom bulk packaging solutions.',
      icon: Package,
      image: 'https://images.pexels.com/photos/5025503/pexels-photo-5025503.jpeg',
      link: '/companies#packaging',
      featured: true,
    },
    {
      name: 'Remas Cleaning Services',
      location: 'Oman',
      description: 'Professional residential, commercial, and industrial cleaning solutions.',
      icon: Sparkles,
      image: 'https://images.pexels.com/photos/4239036/pexels-photo-4239036.jpeg',
      link: '/companies#cleaning',
    },
    {
      name: 'Goodluck Laundry Services',
      location: 'Oman',
      description: 'Premium laundry and dry-cleaning with pickup & delivery services.',
      icon: ShirtIcon,
      image: 'https://images.pexels.com/photos/5591581/pexels-photo-5591581.jpeg',
      link: '/companies#laundry',
    },
  ];

  const stats = [
    { icon: MapPin, value: '2', label: 'Countries' },
    { icon: Users, value: '3', label: 'Companies' },
    { icon: Award, value: '10+', label: 'Years Experience' },
    { icon: Clock, value: '24/7', label: 'Support' },
  ];

  const whyChooseUs = [
    'Industry-leading packaging manufacturing',
    'International presence in India & Oman',
    'High-quality standards & certifications',
    'Reliable and timely delivery',
    'Skilled workforce and management',
    'Competitive pricing',
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center pt-20"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3605420/pexels-photo-3605420.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4 animate-fade-in-up opacity-0 stagger-1">
              Global Excellence in Manufacturing & Services
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6 animate-fade-in-up opacity-0 stagger-2">
              A Global Group Delivering Excellence in{' '}
              <span className="text-gradient">Packaging & Services</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed animate-fade-in-up opacity-0 stagger-3">
              From advanced packaging manufacturing in India to essential services in Oman, AUMKAR Group stands for quality and reliability.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up opacity-0 stagger-4">
              <Link to="/companies">
                <Button
                  data-testid="hero-explore-btn"
                  className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-8 py-6 text-base group"
                >
                  Explore Our Companies
                  <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
                </Button>
              </Link>
              <Link to="/contact?quote=true">
                <Button
                  data-testid="hero-quote-btn"
                  variant="outline"
                  className="border-slate-300 hover:border-blue-700 hover:text-blue-700 rounded-full px-8 py-6 text-base"
                >
                  Get a Quote
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="hero-contact-btn"
                  variant="ghost"
                  className="text-slate-600 hover:text-blue-700 rounded-full px-8 py-6 text-base"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-6 h-6 text-blue-500 mx-auto mb-3" />
                <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divisions Section */}
      <section data-testid="divisions-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4">
              Our Companies
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Three Businesses, One Vision
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From manufacturing to services, our diverse portfolio serves clients across multiple industries.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Featured - Aumkar International */}
            <Card
              data-testid="division-packaging"
              className="md:col-span-8 md:row-span-2 overflow-hidden border-slate-200 card-hover bento-item"
            >
              <div className="relative h-full min-h-[400px]">
                <img
                  src={divisions[0].image}
                  alt={divisions[0].name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                <CardContent className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5" />
                    </div>
                    <span className="text-sm text-blue-300">{divisions[0].location}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-3">{divisions[0].name}</h3>
                  <p className="text-slate-300 mb-6 max-w-lg">{divisions[0].description}</p>
                  <Link to={divisions[0].link}>
                    <Button
                      data-testid="btn-learn-packaging"
                      className="bg-white text-slate-900 hover:bg-slate-100 rounded-full group"
                    >
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>

            {/* Remas Cleaning */}
            <Card
              data-testid="division-cleaning"
              className="md:col-span-4 overflow-hidden border-slate-200 card-hover bento-item"
            >
              <div className="relative h-full min-h-[280px]">
                <img
                  src={divisions[1].image}
                  alt={divisions[1].name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent" />
                <CardContent className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-blue-400" />
                    <span className="text-xs text-blue-300">{divisions[1].location}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{divisions[1].name}</h3>
                  <Link to={divisions[1].link} className="text-sm text-blue-300 hover:text-white flex items-center gap-1">
                    Explore <ArrowRight className="w-3 h-3" />
                  </Link>
                </CardContent>
              </div>
            </Card>

            {/* Goodluck Laundry */}
            <Card
              data-testid="division-laundry"
              className="md:col-span-4 overflow-hidden border-slate-200 card-hover bento-item"
            >
              <div className="relative h-full min-h-[280px]">
                <img
                  src={divisions[2].image}
                  alt={divisions[2].name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent" />
                <CardContent className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <ShirtIcon className="w-5 h-5 text-blue-400" />
                    <span className="text-xs text-blue-300">{divisions[2].location}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{divisions[2].name}</h3>
                  <Link to={divisions[2].link} className="text-sm text-blue-300 hover:text-white flex items-center gap-1">
                    Explore <ArrowRight className="w-3 h-3" />
                  </Link>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="why-choose-section" className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4">
                Why Choose Us
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Excellence in Every Detail
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                With over a decade of experience, AUMKAR Group has established itself as a trusted name in packaging manufacturing and service industries.
              </p>
              <ul className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="inline-block mt-8">
                <Button
                  data-testid="btn-learn-more"
                  variant="outline"
                  className="border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white rounded-full group"
                >
                  Learn More About Us
                  <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-72 h-72 bg-blue-100 rounded-2xl -z-10" />
              <img
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="section-padding bg-blue-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto text-lg">
            Whether you need packaging solutions or professional services, we're here to help your business grow.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact?quote=true">
              <Button
                data-testid="cta-quote-btn"
                className="bg-white text-blue-700 hover:bg-slate-100 rounded-full px-8 py-6 text-base group"
              >
                Get a Free Quote
                <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="cta-contact-btn"
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-base"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
